import * as yup from 'yup';
import { Formik, FormikValues } from 'formik';
import cn from 'classnames';
import { InputBar, Select } from '~/ui';
import form from '~/theme/ui/form.module.scss';
import button from '~/theme/ui/button.module.scss';
import SwitchInput from '~/ui/SwitchInput';
import AddIcon from '~/assets/svg/newSvg/actions/add-icon-empty.svg?react';
import styles from './EditQuestionForm.module.scss';
import FieldGroup from '~/ui/FieldGroup';
import Grade from '~/ui/Grade';
import { getTypeOptions, types } from '../QuestionForm/typeOptions';
import { QuestionItem } from '../QuestionForm';
import ClosedAnswersForm from './ClosedAnswersForm/ClosedAnswersForm';
import { IVariant } from '../QuestionnaireForm';
import { useTranslation } from 'react-i18next';
import { integerRegex } from '~/utils/regex/regex';
import { useEffect } from 'react';

const QuestionSchema = yup.object().shape({
  id: yup.number().optional(),
  name: yup.string().required('question_name_validate'),
  type: yup.number().required(),
  answer: yup.string(),
  required: yup.boolean(),
  multi_answer: yup.boolean(),
  variants: yup.array().optional(),
  sequence_number: yup.string().required('question_number_validate'),
});

export type QuestionValues = yup.InferType<typeof QuestionSchema>;

export interface IFormikValues {
  id: number;
  name: string;
  type: number;
  answer: string;
  required: boolean;
  multi_answer: boolean;
  variants: IVariant[] | [];
  questions_closed_variants?: IVariant[] | [];
  sequence_number: string;
  questions_nums: number[];
}

interface IIndexable {
  [key: string]: number;
}

interface QuestionFormProps {
  question: QuestionItem;
  handleCloseCreate: () => void;
  clickUpdateQuestion: (
    values: any,
    formik: FormikValues,
    initialValues?: QuestionValues
  ) => void;
  questions: QuestionItem[];
}
const EditQuestionForm = ({
  question,
  handleCloseCreate,
  clickUpdateQuestion,
  questions,
}: QuestionFormProps) => {
  const initialSequensNumber = Number(question.sequence_number);

  const initialValues: IFormikValues = {
    id: question.id,
    name: question.name,
    type: (types as IIndexable)[question.type],
    answer: '',
    required: question?.question_data?.required || false,
    multi_answer: question?.question_data?.multi_answer || false,
    variants: question?.question_data?.question_close_variants || [],
    sequence_number: question.sequence_number,
    questions_nums: questions.map(question => Number(question.sequence_number)),
  };
  const { t } = useTranslation();
  const typeOptions = getTypeOptions();

  const handleCloseClick = async (formik: FormikValues) => {
    handleCloseCreate();
    formik.resetForm({ values: initialValues });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={QuestionSchema}
      enableReinitialize
      onSubmit={(values: QuestionValues, formik: FormikValues) => {
        clickUpdateQuestion(values, formik);
      }}
    >
      {formik => {
        useEffect(() => {
          formik.setFieldValue(
            'questions_nums',
            questions.map(question => Number(question.sequence_number))
          );
        }, [questions]);
        return (
          <form className={form.container} onSubmit={formik.handleSubmit}>
            <div className={form.group}>
              <InputBar
                name="sequence_number"
                label={t('question_number')}
                placeholder={t('enter_number')}
                required={true}
                star={true}
                value={formik.values.sequence_number}
                onBlur={formik.handleBlur}
                onChange={e => {
                  if (
                    e.target.value === '' ||
                    integerRegex.test(e.target.value)
                  ) {
                    formik.setFieldValue('sequence_number', e.target.value);
                  }
                }}
                classNameInputWrapper={styles.questionNumberWrapper}
                classNameInput={cn(styles.input, styles.questionNumberInput)}
                error={
                  formik.touched.sequence_number &&
                  (Boolean(formik.errors.sequence_number) ||
                    (initialSequensNumber !==
                      Number(formik.values.sequence_number) &&
                      formik.values.questions_nums.includes(
                        Number(formik.values.sequence_number)
                      )))
                }
                errors={
                  formik.errors.sequence_number === 'number_exists' ||
                  (initialSequensNumber !==
                    Number(formik.values.sequence_number) &&
                    formik.values.questions_nums.includes(
                      Number(formik.values.sequence_number)
                    ))
                    ? t('number_exists')
                    : t(formik.errors.sequence_number || '')
                }
              />
              <InputBar
                name="question"
                label={t('question_title')}
                placeholder={t('question_write')}
                value={formik.values.name}
                disabled={false}
                star={true}
                onChange={event =>
                  formik.setFieldValue('name', event.target.value)
                }
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                errors={t(formik.errors.name || '')}
                classNameInput={styles.input}
              />
              <Select
                name="type"
                label={t('question_type')}
                value={Number(formik.values.type)}
                onChange={value => formik.setFieldValue('type', value)}
                options={typeOptions}
                star={true}
                size="small"
                className={styles.input}
              />
            </div>

            {formik.values.type === types.close && (
              <div className={styles.answersContainer}>
                <ClosedAnswersForm
                  questionId={formik.values.id}
                  variants={formik.values.variants}
                  formik={formik}
                />
              </div>
            )}

            {formik.values.type === types.estimate && (
              <div className={styles.answersContainer}>
                <FieldGroup label={t('estimation')}>
                  {(
                    ['bad', 'tolerable', 'normal', 'good', 'super'] as const
                  ).map(grade => (
                    <FieldGroup.Item key={grade}>
                      <Grade value={grade} />
                    </FieldGroup.Item>
                  ))}
                </FieldGroup>
              </div>
            )}

            <div className={styles.footer}>
              {formik.values.type === types.close && (
                <SwitchInput
                  name="multi_answer"
                  label={t('multiple_answers')}
                />
              )}

              <SwitchInput name="required" label={t('required_question')} />
            </div>

            <div className={cn(form.actions, styles.actions)}>
              <button
                className={button.primary}
                onClick={() => handleCloseClick(formik)}
              >
                {t('cancel')}
              </button>

              <button
                disabled={
                  !formik.isValid ||
                  (initialSequensNumber !==
                    Number(formik.values.sequence_number) &&
                    formik.values.questions_nums.includes(
                      Number(formik.values.sequence_number)
                    ))
                }
                type="submit"
                className={button.success}
              >
                <AddIcon className={button.icon} />
                {t('save_n_exit')}
              </button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default EditQuestionForm;
